import React from 'react';
import Icon from '../../../components/AppIcon';

const ResultsDisplay = ({ results, currentLanguage }) => {
  const translations = {
    en: {
      resultsTitle: "Your Debt Freedom Timeline",
      debtFreeDate: "Debt-Free Date",
      monthsRemaining: "Months Until Freedom",
      statusLabel: "Repayment Status",
      onTrack: "ON TRACK",
      struggling: "STRUGGLING",
      unsustainable: "UNSUSTAINABLE",
      onTrackDesc: "Your debt repayment plan is sustainable and achievable",
      strugglingDesc: "Your repayment timeline is extended - consider increasing payments",
      unsustainableDesc: "Current payment amount is insufficient for reasonable debt payoff"
    },
    es: {
      resultsTitle: "Su Cronograma de Libertad de Deuda",
      debtFreeDate: "Fecha Libre de Deudas",
      monthsRemaining: "Meses Hasta la Libertad",
      statusLabel: "Estado de Reembolso",
      onTrack: "EN CAMINO",
      struggling: "LUCHANDO",
      unsustainable: "INSOSTENIBLE",
      onTrackDesc: "Su plan de pago de deudas es sostenible y alcanzable",
      strugglingDesc: "Su cronograma de reembolso es extendido - considere aumentar los pagos",
      unsustainableDesc: "El monto de pago actual es insuficiente para un pago razonable de la deuda"
    },
    pt: {
      resultsTitle: "Seu Cronograma de Liberdade da Dívida",
      debtFreeDate: "Data Livre de Dívidas",
      monthsRemaining: "Meses Até a Liberdade",
      statusLabel: "Status de Reembolso",
      onTrack: "NO CAMINHO",
      struggling: "LUTANDO",
      unsustainable: "INSUSTENTÁVEL",
      onTrackDesc: "Seu plano de pagamento de dívidas é sustentável e alcançável",
      strugglingDesc: "Seu cronograma de reembolso é estendido - considere aumentar os pagamentos",
      unsustainableDesc: "O valor do pagamento atual é insuficiente para um pagamento razoável da dívida"
    },
    fr: {
      resultsTitle: "Votre Calendrier de Liberté de Dette",
      debtFreeDate: "Date Libre de Dettes",
      monthsRemaining: "Mois Jusqu\'à la Liberté",
      statusLabel: "Statut de Remboursement",
      onTrack: "SUR LA BONNE VOIE",
      struggling: "EN DIFFICULTÉ",
      unsustainable: "NON DURABLE",
      onTrackDesc: "Votre plan de remboursement de dettes est durable et réalisable",
      strugglingDesc: "Votre calendrier de remboursement est prolongé - envisagez d\'augmenter les paiements",
      unsustainableDesc: "Le montant du paiement actuel est insuffisant pour un remboursement raisonnable de la dette"
    },
    de: {
      resultsTitle: "Ihr Schuldenfreiheits-Zeitplan",
      debtFreeDate: "Schuldenfreies Datum",
      monthsRemaining: "Monate Bis zur Freiheit",
      statusLabel: "Rückzahlungsstatus",
      onTrack: "AUF KURS",
      struggling: "KÄMPFEND",
      unsustainable: "NICHT NACHHALTIG",
      onTrackDesc: "Ihr Schuldenrückzahlungsplan ist nachhaltig und erreichbar",
      strugglingDesc: "Ihr Rückzahlungszeitplan ist verlängert - erwägen Sie eine Erhöhung der Zahlungen",
      unsustainableDesc: "Der aktuelle Zahlungsbetrag ist für eine angemessene Schuldenrückzahlung unzureichend"
    },
    it: {
      resultsTitle: "La Tua Tempistica di Libertà dal Debito",
      debtFreeDate: "Data Libera dai Debiti",
      monthsRemaining: "Mesi Fino alla Libertà",
      statusLabel: "Stato di Rimborso",
      onTrack: "IN LINEA",
      struggling: "IN DIFFICOLTÀ",
      unsustainable: "INSOSTENIBILE",
      onTrackDesc: "Il tuo piano di rimborso del debito è sostenibile e raggiungibile",
      strugglingDesc: "La tua tempistica di rimborso è estesa - considera di aumentare i pagamenti",
      unsustainableDesc: "L\'importo del pagamento attuale è insufficiente per un rimborso ragionevole del debito"
    },
    nl: {
      resultsTitle: "Uw Schuldenvrije Tijdlijn",
      debtFreeDate: "Schuldenvrije Datum",
      monthsRemaining: "Maanden Tot Vrijheid",
      statusLabel: "Terugbetalingsstatus",
      onTrack: "OP SCHEMA",
      struggling: "MOEIZAAM",
      unsustainable: "ONHOUDBAAR",
      onTrackDesc: "Uw schuldafbetalingsplan is duurzaam en haalbaar",
      strugglingDesc: "Uw terugbetalingstijdlijn is verlengd - overweeg betalingen te verhogen",
      unsustainableDesc: "Huidig betalingsbedrag is onvoldoende voor redelijke schuldaflossing"
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const getStatusConfig = (status) => {
    const configs = {
      onTrack: {
        label: t?.onTrack,
        description: t?.onTrackDesc,
        bgColor: 'bg-success/10',
        textColor: 'text-success',
        borderColor: 'border-success',
        icon: 'CheckCircle'
      },
      struggling: {
        label: t?.struggling,
        description: t?.strugglingDesc,
        bgColor: 'bg-warning/10',
        textColor: 'text-warning',
        borderColor: 'border-warning',
        icon: 'AlertTriangle'
      },
      unsustainable: {
        label: t?.unsustainable,
        description: t?.unsustainableDesc,
        bgColor: 'bg-error/10',
        textColor: 'text-error',
        borderColor: 'border-error',
        icon: 'XCircle'
      }
    };
    return configs?.[status];
  };

  const statusConfig = getStatusConfig(results?.status);

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg shadow-elevation-2 p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6 text-center">
          {t?.resultsTitle}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          <div className="bg-muted rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
              <p className="text-sm text-muted-foreground">{t?.debtFreeDate}</p>
            </div>
            <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
              {results?.debtFreeDate}
            </p>
          </div>

          <div className="bg-muted rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              <p className="text-sm text-muted-foreground">{t?.monthsRemaining}</p>
            </div>
            <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
              {results?.monthsRemaining}
            </p>
          </div>
        </div>

        <div className={`${statusConfig?.bgColor} ${statusConfig?.borderColor} border-2 rounded-lg p-4 md:p-6`}>
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 ${statusConfig?.textColor}`}>
              <Icon name={statusConfig?.icon} size={24} />
            </div>
            <div className="flex-1">
              <p className="text-xs md:text-sm text-muted-foreground mb-1">{t?.statusLabel}</p>
              <p className={`text-lg md:text-xl font-heading font-semibold ${statusConfig?.textColor} mb-2`}>
                {statusConfig?.label}
              </p>
              <p className="text-sm md:text-base text-foreground">
                {statusConfig?.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;